import React, { useState, useRef } from "react";
import { QRCode } from "react-qrcode-logo";

const QRCodeGenerator = () => {
  const [url, setUrl] = useState();
  const [qrStyle, setQrStyle] = useState("dots");
  const [fgColor, setFgColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [eyeColor, setEyeColor] = useState("#ff0000");
  const [eyeRadius, setEyeRadius] = useState(5);
  const [size, setSize] = useState(250);
  const [logoImage, setLogoImage] = useState(null);
  const [logoOpacity, setLogoOpacity] = useState(1);
  const [logoHeight, setLogoHeight] = useState(60);
  const [logoWidth, setLogoWidth] = useState(60);
  const [logoPadding, setLogoPadding] = useState(4);
  const [logoPaddingRadius, setLogoPaddingRadius] = useState(false);
  const [logoPaddingStyle, setLogoPaddingStyle] = useState("circle");
  const [removeBehindLogo, setRemoveBehindLogo] = useState(true);
  const [ecLevel, setEcLevel] = useState("H");

  const qrRef = useRef(null);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => setLogoImage(event.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleDownload = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (canvas) {
      const link = document.createElement("a");
      link.download = "my_qrcode.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-700 flex flex-col items-center justify-center px-4 py-10">
      <div className="max-w-5xl w-full bg-white/10 backdrop-blur-md p-6 sm:p-10 rounded-3xl shadow-lg text-center border border-white/20">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-6">
          Advanced QR Code Generator
        </h1>
        <p className="text-white/70 mb-8">
          Customize every detail of your QR Code - logo, colors, styles, and more.
        </p>

        {/* --- Input Controls --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 text-left">
          {/* URL */}
          <div>
            <label className="text-white mb-2 block">Enter text or URL</label>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-white/20 text-white focus:ring-2 focus:ring-rose-400 focus:outline-none"
              placeholder="https://example.com"
            />
          </div>

          {/* QR Style */}
          <div>
            <label className="text-white mb-2 block">QR Style</label>
            <select
              value={qrStyle}
              onChange={(e) => setQrStyle(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-white/20 text-white focus:ring-2 focus:ring-rose-400 focus:outline-none"
            >
              <option className="text-black" value="dots">Dots</option>
              <option className="text-black" value="squares">Squares</option>
            </select>
          </div>

          {/* Foreground */}
          <div>
            <label className="text-white mb-2 block">Foreground Color</label>
            <input
              type="color"
              value={fgColor}
              onChange={(e) => setFgColor(e.target.value)}
              className="w-full h-10 cursor-pointer rounded-lg"
            />
          </div>

          {/* Background */}
          <div>
            <label className="text-white mb-2 block">Background Color</label>
            <input
              type="color"
              value={bgColor}
              onChange={(e) => setBgColor(e.target.value)}
              className="w-full h-10 cursor-pointer rounded-lg"
            />
          </div>

          {/* Eye Color */}
          <div>
            <label className="text-white mb-2 block">Eye Color</label>
            <input
              type="color"
              value={eyeColor}
              onChange={(e) => setEyeColor(e.target.value)}
              className="w-full h-10 cursor-pointer rounded-lg"
            />
          </div>

          {/* Eye Radius */}
          <div>
            <label className="text-white mb-2 block">Eye Radius</label>
            <input
              type="number"
              min="0"
              max="20"
              value={eyeRadius}
              onChange={(e) => setEyeRadius(Number(e.target.value))}
              className="w-full px-4 py-2 rounded-lg bg-white/20 text-white focus:ring-2 focus:ring-rose-400 focus:outline-none"
            />
          </div>

          {/* Size */}
          <div>
            <label className="text-white mb-2 block">QR Size (px)</label>
            <input
              type="number"
              min="100"
              max="600"
              value={size}
              onChange={(e) => setSize(Number(e.target.value))}
              className="w-full px-4 py-2 rounded-lg bg-white/20 text-white focus:ring-2 focus:ring-rose-400 focus:outline-none"
            />
          </div>

          {/* Error Correction Level */}
          <div>
            <label className="text-white mb-2 block">Error Correction Level</label>
            <select
              value={ecLevel}
              onChange={(e) => setEcLevel(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-white/20 text-white focus:ring-2 focus:ring-rose-400 focus:outline-none"
            >
              <option className="text-black" value="L">L (Low)</option>
              <option className="text-black" value="M">M (Medium)</option>
              <option className="text-black" value="Q">Q (Quartile)</option>
              <option className="text-black" value="H">H (High)</option>
            </select>
          </div>

          {/* Logo Upload */}
          <div>
            <label className="text-white mb-2 block">Upload Logo Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="w-full text-white bg-white/10 p-2 rounded-lg file:mr-3 file:py-1 file:px-3 file:rounded-full file:border-0 file:bg-rose-500 file:text-white hover:file:bg-rose-600"
            />
          </div>

          {/* Logo Options */}
          <div className="flex flex-col gap-3">
            <label className="text-white flex items-center gap-2">
              <input
                type="checkbox"
                checked={removeBehindLogo}
                onChange={() => setRemoveBehindLogo(!removeBehindLogo)}
              />
              Remove background behind logo
            </label>

            <label className="text-white flex items-center gap-2">
              <input
                type="checkbox"
                checked={logoPaddingRadius}
                onChange={() => setLogoPaddingRadius(!logoPaddingRadius)}
              />
              Enable logo padding radius
            </label>
          </div>

          {/* Logo Padding */}
          <div>
            <label className="text-white mb-2 block">Logo Padding</label>
            <input
              type="number"
              min="0"
              max="20"
              value={logoPadding}
              onChange={(e) => setLogoPadding(Number(e.target.value))}
              className="w-full px-4 py-2 rounded-lg bg-white/20 text-white focus:ring-2 focus:ring-rose-400 focus:outline-none"
            />
          </div>

          {/* Logo Padding Style */}
          <div>
            <label className="text-white mb-2 block">Logo Padding Style</label>
            <select
              value={logoPaddingStyle}
              onChange={(e) => setLogoPaddingStyle(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-white/20 text-white focus:ring-2 focus:ring-rose-400 focus:outline-none"
            >
              <option className="text-black" value="circle">Circle</option>
              <option className="text-black" value="square">Square</option>
            </select>
          </div>

          {/* Logo Opacity */}
          <div>
            <label className="text-white mb-2 block">Logo Opacity (0–1)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={logoOpacity}
              onChange={(e) => setLogoOpacity(Number(e.target.value))}
              className="w-full px-4 py-2 rounded-lg bg-white/20 text-white focus:ring-2 focus:ring-rose-400 focus:outline-none"
            />
          </div>

          {/* Logo Height & Width */}
          <div>
            <label className="text-white mb-2 block">Logo Height (px)</label>
            <input
              type="number"
              min="20"
              max="200"
              value={logoHeight}
              onChange={(e) => setLogoHeight(Number(e.target.value))}
              className="w-full px-4 py-2 rounded-lg bg-white/20 text-white focus:ring-2 focus:ring-rose-400 focus:outline-none"
            />
          </div>

          <div>
            <label className="text-white mb-2 block">Logo Width (px)</label>
            <input
              type="number"
              min="20"
              max="200"
              value={logoWidth}
              onChange={(e) => setLogoWidth(Number(e.target.value))}
              className="w-full px-4 py-2 rounded-lg bg-white/20 text-white focus:ring-2 focus:ring-rose-400 focus:outline-none"
            />
          </div>
        </div>

        {/* --- QR Preview --- */}
        <div ref={qrRef} className="flex flex-col items-center justify-center">
          <div className="bg-white p-4 rounded-2xl shadow-md inline-block">
            <QRCode
              value={url}
              size={size}
              qrStyle={qrStyle}
              fgColor={fgColor}
              bgColor={bgColor}
              eyeRadius={eyeRadius}
              eyeColor={eyeColor}
              logoImage={logoImage}
              logoOpacity={logoOpacity}
              logoWidth={logoWidth}
              logoHeight={logoHeight}
              logoPadding={logoPadding}
              logoPaddingStyle={logoPaddingStyle}
              logoPaddingRadius={logoPaddingRadius}
              removeQrCodeBehindLogo={removeBehindLogo}
              ecLevel={ecLevel}
            />
          </div>

          <button
            onClick={handleDownload}
            className="mt-6 px-6 py-3 rounded-full bg-rose-500 hover:bg-rose-600 text-white font-semibold shadow-md transition-all"
          >
            Download QR Code
          </button>
        </div>
      </div>

      <footer className="mt-10 text-white/70 text-sm text-center">
        © {new Date().getFullYear()} Built with ❤️
      </footer>
    </div>
  );
};

export default QRCodeGenerator;
